import { getSlug } from "@huub-web/utils";

import type { NavigationContext } from "../../contexts";
import type { NavigationState } from "../../types";
import { traverseNavigationState } from "./traverseNavigationState";

/**
 * Searches through navigationState to find a matching route based on navigation state.
 * Handles dynamic variables like [id] by matching them with actual values from navigationContext.
 *
 * @param navigationState - The application state containing all navigation records
 * @param navigationContext - The current navigation state keys (e.g., ["orientation", "0226010000036236"])
 * @returns The matching path or null if no match is found
 */
export const getPathFromNavigationState = (
  navigationState: NavigationState,
  navigationContext: NavigationContext,
): string | null => {
  if (
    !navigationContext ||
    navigationContext.length === 0 ||
    !navigationState
  ) {
    return null;
  }

  // console.log(
  //   "[getPathFromNavigationState]",
  //   navigationState,
  //   navigationContext,
  // );

  const pathSegments: string[] = [];

  const result = traverseNavigationState(
    navigationState,
    navigationContext,
    ({ key, remainingKeys, traversalResult }) => {
      const slug = getSlug(traversalResult.slug, ["[", "]"]);

      // Replace dynamic variables in slug with actual values
      let processedSlug = slug;

      // Check if the entire slug is a dynamic variable
      if (slug.startsWith("[") && slug.endsWith("]")) {
        // Use the actual value from navigationContext
        processedSlug = key;
      } else if (slug.includes("[") && slug.includes("]")) {
        // Handle slugs with dynamic parts like "room-[roomId]"
        // Replace all dynamic placeholders with the current key value
        processedSlug = slug.replace(/\[([^\]]+)\]/g, key);
      }

      pathSegments.push(processedSlug);

      // If no more keys to process, we're done
      if (remainingKeys.length === 0) {
        return { continue: false, result: `/${pathSegments.join("/")}` };
      }

      // Continue traversing
      return { continue: true };
    },
  );

  return result;
};
